import AddressListPanel from 'Components/Generic/Address/AddressListPanel';
import { showSuccessToast } from 'Components/Generic/BOSToasterUtil';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import StatusToggleButton from 'Components/Generic/Buttons/StatusToggleButton';
import OrgRolesPanel from 'Components/Generic/Roles/OrgRolesPanel';
import ChargeAccountData from 'Data/ChargeAccountData';
import { useAlertContext } from 'Data/context/Alerts';
import { OBJECT_STATUS, user_org_roles, user_roles } from 'Data/Enum';
import GenericTableData from 'Data/GenericTableData';
import OrgData from 'Data/OrgData';
import SiteDrawerData from 'Data/SiteDrawerData';
import DataManager from 'Lib/DataManager';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import BudgetsSetupPanel from 'Pages/Accounts/Budgets/BudgetsSetupPanel';
import AccountsListPanel from 'Pages/Accounts/ChargeAccounts/AccountsListPanel';
import ResourceListPanel from 'Pages/Resources/ResourceListPanel';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styles from 'Styles/styles.module.css';
import { isUserAuthorized } from 'Util';
import { getTagValue } from 'Util/tagUtil';
import OrganizationInformationPanel from '../Organizations/OrganizationInformationPanel';

let DM = new DataManager();
let OrgAddressData = GenericTableData.getTableDataBus(
  'ORG_ADD',
  'organization_addresses',
);

const OrganizationDetails = ({
  orgId,
  params,
  editable = false,
  compact = false,
  budgetView = false,
  ...props
}) => {
  const [org, setOrg, orgRef] = useStateWithRef({});
  const [selectedAccount, setSelectedAccount] = useState();
  const [addressIdList, setAddressIdList] = useState([]);

  const { openConfirmationDialog } = useAlertContext();

  useEffect(() => {
    DM.add(
      OrgData.registerListener('BLANK_RECORD_LOADED', onOrgLoaded),
      OrgData,
    );
    DM.add(OrgData.registerListener('RECORD_LOADED', onOrgLoaded), OrgData);
    DM.add(
      OrgData.registerListener('RECORD_ACTIVATED', onOrgActivated),
      OrgData,
    );
    DM.add(
      OrgData.registerListener('RECORD_DEACTIVATED', onOrgDeactivated),
      OrgData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (!orgId && !params?.id) {
      OrgData.getBlank();
    }
  }, []);

  useEffect(() => {
    if (orgId && orgId > 0) {
      getOrg(orgId);
      ChargeAccountData.fetchAccountsForOrg(orgId);
      setSelectedAccount(undefined);
    }
  }, [orgId]);

  useEffect(() => {
    if (params?.id && !orgId) {
      getOrg(params.id);
      ChargeAccountData.fetchAccountsForOrg(params.id);
      setSelectedAccount(undefined);
    }
  }, [params]);

  const getOrg = (id) => {
    OrgData.getOrgAndAssociations(id);
  };

  const onOrgLoaded = (payload) => {
    if (payload) {
      setOrg(payload);
    }
  };

  useEffect(() => {
    if (org.addresses?.length) {
      let idList = org.addresses.map((add) => add.id);
      setAddressIdList(idList);
    } else {
      setAddressIdList([]);
    }
  }, [org]);

  const onSaveAddress = (isNew, savedAddress) => {
    if (isNew) {
      OrgAddressData.createRecord({
        address_id: savedAddress.id,
        organization_id: orgRef.current.id,
      });
    }
    getOrg(orgRef.current.id);
  };

  const deleteAddressLinks = (addressId) => {
    if (addressId) {
      OrgAddressData.deleteRecordsWhere({
        address_id: addressId,
        organization_id: orgRef.current.id,
      });
    }
  };

  let statusCancelButton =
    editable &&
    !budgetView &&
    isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR],
    }) &&
    (org?.id ? (
      <StatusToggleButton
        currentStatus={org.status}
        onStatusToggleClicked={() => {
          statusToggleClicked();
        }}
      />
    ) : (
      <CancelButton onCancel={SiteDrawerData.close} />
    ));

  const toggleOrgStatus = () => {
    orgRef.current?.status === OBJECT_STATUS.ACTIVE.key
      ? OrgData.deactivateRecord(orgRef.current?.id)
      : OrgData.activateRecord(orgRef.current?.id);
  };

  const statusToggleClicked = () => {
    let currentStatusActive =
      orgRef.current?.status === OBJECT_STATUS.ACTIVE.key;
    openConfirmationDialog({
      onConfirm: toggleOrgStatus,
      dialogTitle: currentStatusActive
        ? 'Deactivate Core Facility?'
        : 'Reactivate Core Facility?',
      icon: currentStatusActive ? 'disable' : undefined,
      intent: currentStatusActive ? 'danger' : 'primary',
    });
  };

  const onOrgActivated = (payload) => {
    showSuccessToast('Core Facility Reactivated');
    getOrg(payload.id);
  };

  const onOrgDeactivated = (payload) => {
    showSuccessToast('Core Facility Deactivated');
    getOrg(payload.id);
  };

  return (
    org && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
        }}>
        <div
          className={styles.panel_flex_box}
          style={{
            ...(compact && { flexDirection: 'column' }),
          }}>
          {!budgetView && (
            <OrganizationInformationPanel
              title='Details'
              editable={editable}
              {...props}
            />
          )}

          {!budgetView && (
            <AddressListPanel
              addressIdList={addressIdList}
              deleteAddressLinks={deleteAddressLinks}
              editable={editable && !!org?.id}
              canAddAddress={editable && !!org?.id}
              disabled={!org?.id}
              addressType='Core'
              onSaveAddress={onSaveAddress}
            />
          )}

          {!budgetView &&
            isUserAuthorized({
              roles: [
                user_roles.ADMINISTRATOR,
                user_roles.PERMISSIONS_ADMINISTRATOR,
                [org?.id, user_org_roles.MANAGER.key],
              ],
            }) && <OrgRolesPanel orgId={org?.id} editable={editable} />}

          {!budgetView &&
            (!!getTagValue(org, 'line_item_resources') ||
              org?.resources?.length > 0) && (
              <ResourceListPanel
                orgOptionNotSet={!getTagValue(org, 'line_item_resources')}
                orgId={org?.id}
                editable={editable}
              />
            )}

          {isUserAuthorized({
            roles: [
              user_roles.ADMINISTRATOR,
              user_roles.BUDGETER,
              [org?.id, user_org_roles.MANAGER.key],
            ],
          }) && (
            <div className={styles.full_width_panel}>
              <AccountsListPanel
                editable={
                  editable &&
                  !!org?.id &&
                  isUserAuthorized({
                    roles: [user_roles.ADMINISTRATOR, user_roles.BUDGETER],
                  })
                }
                disabled={!org?.id}
                compact={compact}
                orgId={org.id}
                selectedAccount={selectedAccount}
                fieldsForNewAccount={{
                  parent_organization_id: org.id,
                  pi_id: org.pi_id,
                }}
                onSelectAccount={(account) => {
                  setSelectedAccount(account);
                }}
              />
            </div>
          )}

          {isUserAuthorized({
            roles: [
              user_roles.ADMINISTRATOR,
              user_roles.RECONCILER,
              user_roles.BUYER,
              user_roles.TRANSACTOR,
              [org?.id, user_org_roles.MANAGER.key],
            ],
          }) && (
            <div className={styles.full_width_panel}>
              <BudgetsSetupPanel
                editable={
                  editable &&
                  !!org?.id &&
                  isUserAuthorized({
                    roles: [user_roles.ADMINISTRATOR, user_roles.BUDGETER],
                  })
                }
                disabled={!org?.id}
                compact={compact}
                org={org}
                selectedAccount={selectedAccount}
                fieldsForNewBudget={{
                  parent_organization_id: org.id,
                }}
              />
            </div>
          )}
        </div>
        {statusCancelButton}
      </div>
    )
  );
};

OrganizationDetails.propTypes = {
  orgId: PropTypes.number,
  params: PropTypes.shape({ id: PropTypes.string }),
  editable: PropTypes.bool,
  compact: PropTypes.bool,
  budgetView: PropTypes.bool,
};

export default OrganizationDetails;
